import React, { useEffect, useId, useRef, useState } from "react";
import { previewLifecycle, ORCHESTRATION_HANDOFF_STATUS_LABELS } from "../../lib/engineeringLifecyclePreviewClient";

// SAV-92A -- read-only Engineering Lifecycle Preview UI.
//
// This component can only PREVIEW a lifecycle transition via the backend's
// read-only proxy route. It has no authorization, commit, dispatch, or
// execution capability. The "Execute" control below is permanently disabled,
// has no click handler, and calls no client of any kind -- it exists only to
// show operators what is coming in a later release phase.

export const EXECUTE_UNAVAILABLE_EXPLANATION =
    "Execution is not enabled yet. Preview is currently read-only. Human authorization, Commit, and bounded execution will be enabled in later release phases.";

const WORKFLOW_STEPS = [
    { key: "observe", label: "Observe", available: true },
    { key: "preview", label: "Preview", available: true },
    { key: "authorize", label: "Authorize", available: false },
    { key: "commit", label: "Commit", available: false },
    { key: "execute", label: "Execute", available: false },
    { key: "inspect", label: "Inspect", available: false },
];

function WorkflowSteps() {
    return (
        <ol className="ed-preview-workflow" aria-label="Engineering lifecycle workflow">
            {WORKFLOW_STEPS.map((step) => (
                <li
                    key={step.key}
                    className={`ed-preview-step ${step.available ? "ed-preview-step--available" : "ed-preview-step--future"}${step.key === "preview" ? " ed-preview-step--current" : ""}`}
                >
                    <span className="ed-preview-step-label">{step.label}</span>
                    <span className="ed-preview-step-note">
                        {step.available ? (step.key === "preview" ? "Current step" : "Available") : "Not yet available"}
                    </span>
                </li>
            ))}
        </ol>
    );
}

// SAV-92A remediation -- bounded evidence display only.
//
// This renders exactly the narrow, allowlisted fields the client already
// extracted in `evidence` (see extractBoundedEvidence in
// engineeringLifecyclePreviewClient.js). It never touches the raw upstream
// response object and never does a generic Object.entries/JSON.stringify walk
// over it, so any future/unexpected backend field -- a stack trace, a service
// account, a Firestore path, a token -- can never reach the DOM through this
// component, even if it appears on the raw response.
function BoundedEvidence({ evidence }) {
    if (!evidence) return null;

    const rows = [];
    if (typeof evidence.orchestrationStatus === "string" && evidence.orchestrationStatus) {
        rows.push(["Orchestration status", evidence.orchestrationStatus]);
    }
    if (typeof evidence.contractVersion === "string" && evidence.contractVersion) {
        rows.push(["Contract version", evidence.contractVersion]);
    }
    if (typeof evidence.proposedHandoffRole === "string" && evidence.proposedHandoffRole) {
        rows.push(["Proposed handoff role", evidence.proposedHandoffRole]);
    }
    if (Array.isArray(evidence.reasons) && evidence.reasons.length) {
        rows.push(["Reasons", evidence.reasons.join(", ")]);
    }
    if (typeof evidence.decisionStatus === "string" && evidence.decisionStatus) {
        rows.push(["Decision status", evidence.decisionStatus]);
    }
    if (!rows.length) return null;

    return (
        <details className="ed-diagnostic ed-preview-raw">
            <summary>Technical evidence</summary>
            <dl>
                {rows.map(([label, value]) => (
                    <React.Fragment key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                    </React.Fragment>
                ))}
            </dl>
        </details>
    );
}

// SAV-92A functional remediation -- "Handoff preview" is a separate,
// non-authority panel shown only alongside an Allowed lifecycle decision. It
// renders orchestration_status using only the proven handoff vocabulary; an
// unrecognized/future value still renders (bounded, since it is already a
// plain allowlisted string) but is never claimed to be one of the known
// handoff categories.
function HandoffPreview({ evidence }) {
    const status = evidence && typeof evidence.orchestrationStatus === "string" ? evidence.orchestrationStatus : null;
    if (!status) return null;
    const label = ORCHESTRATION_HANDOFF_STATUS_LABELS[status] || status;
    return (
        <div className="ed-preview-handoff">
            <span className="ed-section-label">Handoff preview</span>
            <strong>{label}</strong>
            {typeof evidence.proposedHandoffRole === "string" && evidence.proposedHandoffRole ? (
                <p className="ed-muted">Proposed role: {evidence.proposedHandoffRole}</p>
            ) : null}
        </div>
    );
}

// Bounded evaluator status/reason detail shown alongside a Blocked lifecycle
// decision -- never the Handoff preview, which only applies to Allowed.
function EvaluatorStatus({ evidence }) {
    if (!evidence) return null;
    const hasStatus = typeof evidence.decisionStatus === "string" && evidence.decisionStatus;
    const hasReasons = Array.isArray(evidence.reasons) && evidence.reasons.length;
    if (!hasStatus && !hasReasons) return null;
    return (
        <div className="ed-preview-evaluator-status">
            {hasStatus ? <p><span className="ed-section-label">Evaluator status</span>{evidence.decisionStatus}</p> : null}
            {hasReasons ? <p><span className="ed-section-label">Reasons</span>{evidence.reasons.join(", ")}</p> : null}
        </div>
    );
}

// Maps a normalized preview result onto one of this codebase's existing
// StatusBadge tone classes (ed-status--healthy/warning/danger/neutral).
// This is display-only -- it never feeds back into any request.
function resultTone(kind) {
    if (kind === "allowed") return "healthy";
    if (kind === "blocked") return "warning";
    return "danger"; // invalid, auth, unavailable, network, malformed
}

function PreviewResultPanel({ result }) {
    if (!result) return null;
    const tone = resultTone(result.kind);

    if (result.kind === "allowed") {
        return (
            <div className={`ed-preview-result ed-status--${tone}`} role="status">
                <span className="ed-section-label">Lifecycle evaluation</span>
                <strong className="ed-preview-result-title">Allowed</strong>
                <p>The backend's authoritative lifecycle decision (transition_decision.allowed) reported this transition as allowed. This is a preview only -- no lifecycle state has been changed, and nothing has been authorized, committed, or executed.</p>
                <HandoffPreview evidence={result.evidence} />
                <BoundedEvidence evidence={result.evidence} />
            </div>
        );
    }
    if (result.kind === "blocked") {
        return (
            <div className={`ed-preview-result ed-status--${tone}`} role="status">
                <span className="ed-section-label">Lifecycle evaluation</span>
                <strong className="ed-preview-result-title">Blocked</strong>
                <p>The backend's authoritative lifecycle decision (transition_decision.allowed) reported this transition as blocked. This is a deterministic domain outcome, not an application error.</p>
                <EvaluatorStatus evidence={result.evidence} />
                <BoundedEvidence evidence={result.evidence} />
            </div>
        );
    }
    if (result.kind === "invalid") {
        return (
            <div className={`ed-preview-result ed-status--${tone}`} role="alert">
                <strong className="ed-preview-result-title">Preview request invalid</strong>
                <p>{result.message}</p>
                {Array.isArray(result.missingFields) && result.missingFields.length ? (
                    <p className="ed-muted">Missing: {result.missingFields.join(", ")}</p>
                ) : null}
                <BoundedEvidence evidence={result.evidence} />
            </div>
        );
    }
    if (result.kind === "auth") {
        return (
            <div className={`ed-preview-result ed-status--${tone}`} role="alert">
                <strong className="ed-preview-result-title">Not authorized to preview</strong>
                <p>{result.message}</p>
            </div>
        );
    }
    if (result.kind === "malformed") {
        return (
            <div className={`ed-preview-result ed-status--${tone}`} role="alert">
                <strong className="ed-preview-result-title">Preview response could not be interpreted</strong>
                <p>{result.message}</p>
                <BoundedEvidence evidence={result.evidence} />
            </div>
        );
    }
    // network / unavailable
    return (
        <div className={`ed-preview-result ed-status--${tone}`} role="alert">
            <strong className="ed-preview-result-title">Preview unavailable</strong>
            <p>{result.message || "Engineering Intelligence preview is currently unavailable."}</p>
            <span className="ed-muted">The saVVy Broker application remains available. No lifecycle state was changed.</span>
        </div>
    );
}

// SAV-92A functional remediation -- auto-resolved lifecycle instance.
//
// resolvedLifecycleInstance comes from resolveLifecycleInstanceId() in
// engineeringLifecycleInstanceResolver.js, applied by the caller against the
// specific work_item_key this control was rendered for. It is one of:
//   { status: "resolved", lifecycleInstanceId, source }
//   { status: "unavailable", lifecycleInstanceId: null, source: null }
//   { status: "ambiguous", lifecycleInstanceId: null, source: null }
// The operator never types or invents this value, and Preview cannot run
// unless a single, unambiguous, matching instance was resolved.
const DEFAULT_RESOLVED_INSTANCE = { status: "unavailable", lifecycleInstanceId: null, source: null };

export default function LifecyclePreviewControl({
    workItemKey,
    resolvedLifecycleInstance = DEFAULT_RESOLVED_INSTANCE,
    initialOpen = false,
    initialPhase = "idle",
    initialResult = null,
}) {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const [event, setEvent] = useState("");
    const [phase, setPhase] = useState(initialPhase); // idle | loading | result
    const [result, setResult] = useState(initialResult);
    const triggerRef = useRef(null);
    const firstFieldRef = useRef(null);
    const titleId = useId();
    const executeHelpId = useId();
    const instanceHelpId = useId();

    const instanceStatus = resolvedLifecycleInstance?.status || "unavailable";
    const lifecycleInstanceId = instanceStatus === "resolved" ? resolvedLifecycleInstance.lifecycleInstanceId : "";
    const canPreview = instanceStatus === "resolved";

    useEffect(() => {
        if (isOpen && firstFieldRef.current) firstFieldRef.current.focus();
    }, [isOpen]);

    if (!workItemKey) return null;

    const close = () => {
        setIsOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
    };

    const onDialogKeyDown = (keyEvent) => {
        if (keyEvent.key === "Escape") close();
    };

    const onSubmit = async (formEvent) => {
        formEvent.preventDefault();
        if (!canPreview) return;
        setPhase("loading");
        setResult(null);
        const outcome = await previewLifecycle({ workItemKey, lifecycleInstanceId, event });
        setResult(outcome);
        setPhase("result");
    };

    return (
        <div className="ed-preview-control">
            <button type="button" className="ed-preview-trigger" ref={triggerRef} onClick={() => setIsOpen(true)}>
                Preview lifecycle transition
            </button>
            {isOpen ? (
                <div className="ed-modal-overlay ed-preview-overlay" onClick={close}>
                    <div
                        className="ed-modal ed-preview-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                        onKeyDown={onDialogKeyDown}
                    >
                        <div className="ed-preview-header">
                            <h3 id={titleId}>Preview lifecycle transition</h3>
                            <button type="button" className="ed-modal-close" aria-label="Close preview" onClick={close}>×</button>
                        </div>
                        <WorkflowSteps />
                        <form onSubmit={onSubmit} className="ed-preview-form">
                            <label className="ed-cycle-field">
                                Work item
                                <input value={workItemKey} readOnly disabled aria-readonly="true" />
                            </label>
                            <label className="ed-cycle-field">
                                Lifecycle instance
                                <input
                                    value={lifecycleInstanceId}
                                    readOnly
                                    disabled
                                    aria-readonly="true"
                                    aria-describedby={!canPreview ? instanceHelpId : undefined}
                                    placeholder={canPreview ? undefined : "No lifecycle instance available"}
                                />
                            </label>
                            {!canPreview ? (
                                <p id={instanceHelpId} className="ed-muted ed-preview-instance-help">
                                    {instanceStatus === "ambiguous"
                                        ? "Multiple conflicting lifecycle instance IDs were found for this work item. Preview cannot run until this is resolved."
                                        : "No lifecycle instance is available for this work item yet. Preview cannot run until a matching instance is available."}
                                </p>
                            ) : null}
                            <label className="ed-cycle-field">
                                Requested event
                                <input
                                    ref={firstFieldRef}
                                    value={event}
                                    onChange={(changeEvent) => setEvent(changeEvent.target.value)}
                                    placeholder="e.g. architecture_review_requested"
                                    required
                                />
                            </label>
                            <p className="ed-muted ed-preview-event-help">
                                Enter a canonical lifecycle event. Available-event discovery is not yet exposed by the Preview API.
                            </p>
                            <button type="submit" className="ed-cycle-button" disabled={phase === "loading" || !canPreview}>
                                {phase === "loading" ? "Running preview…" : "Run Preview"}
                            </button>
                        </form>
                        {phase === "loading" ? (
                            <div className="ed-panel ed-state ed-preview-loading">
                                <span className="ed-status ed-status--neutral">loading</span>
                                <strong>Requesting a read-only lifecycle preview…</strong>
                            </div>
                        ) : null}
                        {phase === "result" ? <PreviewResultPanel result={result} /> : null}
                        <div className="ed-preview-execute-row">
                            <button type="button" className="ed-preview-execute" disabled aria-describedby={executeHelpId} title={EXECUTE_UNAVAILABLE_EXPLANATION}>
                                Execute
                            </button>
                            <p id={executeHelpId} className="ed-preview-execute-help">{EXECUTE_UNAVAILABLE_EXPLANATION}</p>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
